import type { Connection } from '../connection';
import { type Storage } from './storage';

export interface BlobRecord {
  key: string;
  /**
   * Inline bytes for legacy/small blobs. Missing means metadata-only or
   * source-backed; callers must not treat missing bytes as an empty file.
   */
  data?: Uint8Array;
  /**
   * Browser-native blob source used for upload without materialising a
   * Uint8Array copy in IndexedDB or runner JSON. Not persisted by IDB.
   */
  source?: Blob;
  mime: string;
  size?: number;
  createdAt?: Date;
}

export function getBlobRecordSize(blob: BlobRecord): number {
  return blob.size ?? blob.source?.size ?? blob.data?.byteLength ?? 0;
}

export function hasBlobRecordInlineBytes(
  blob: BlobRecord
): blob is BlobRecord & { data: Uint8Array } {
  return (blob.data?.byteLength ?? 0) > 0;
}

export interface ListedBlobRecord {
  key: string;
  mime: string;
  size: number;
  createdAt?: Date;
}

export interface BlobStorage extends Storage {
  readonly storageType: 'blob';
  readonly isReadonly: boolean;
  get(key: string, signal?: AbortSignal): Promise<BlobRecord | null>;
  set(blob: BlobRecord, signal?: AbortSignal): Promise<void>;
  delete(
    key: string,
    permanently: boolean,
    signal?: AbortSignal
  ): Promise<void>;
  release(signal?: AbortSignal): Promise<void>;
  list(signal?: AbortSignal): Promise<ListedBlobRecord[]>;
}

export abstract class BlobStorageBase implements BlobStorage {
  readonly storageType = 'blob';
  abstract readonly connection: Connection;
  abstract readonly isReadonly: boolean;
  abstract get(key: string, signal?: AbortSignal): Promise<BlobRecord | null>;
  abstract set(blob: BlobRecord, signal?: AbortSignal): Promise<void>;
  abstract delete(
    key: string,
    permanently: boolean,
    signal?: AbortSignal
  ): Promise<void>;
  abstract release(signal?: AbortSignal): Promise<void>;
  abstract list(signal?: AbortSignal): Promise<ListedBlobRecord[]>;
}
